import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  getUserId,
  getUserLanguage,
  setUserLanguage,
  getTempLanguage,
  setTempLanguage,
} from "../api/sessionManagement";
import { changeUserLanguageOnServer } from "../api/userLanguage";

let cachedUserLanguage: string | null = null;
let lastUserId: number | null = null;

export const clearUserLanguageCache = () => {
  cachedUserLanguage = null;
  lastUserId = null;
};

export const updateUserLanguageCache = (userId: number, language: string) => {
  cachedUserLanguage = language;
  lastUserId = userId;
};

export const useUserLanguage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [userId, setUserId] = useState<number | null>(() => getUserId());

  useEffect(() => {
    const checkUserId = () => {
      const currentUserId = getUserId();
      if (currentUserId !== userId) {
        setUserId(currentUserId);
      }
    };

    window.addEventListener("localStorageUpdate", checkUserId);
    return () => window.removeEventListener("localStorageUpdate", checkUserId);
  }, [userId]);

  useEffect(() => {
    const initializeLanguage = async () => {
      if (userId) {
        if (lastUserId !== userId) {
          cachedUserLanguage = getUserLanguage(userId);
          lastUserId = userId;
        }

        if (cachedUserLanguage && i18n.language !== cachedUserLanguage) {
          await i18n.changeLanguage(cachedUserLanguage);
        }
      } else {
        const tempLang = getTempLanguage();
        if (tempLang && i18n.language !== tempLang) {
          await i18n.changeLanguage(tempLang);
        } else if (!tempLang) {
          // Не переключаем на язык браузера автоматически, если язык уже установлен
          // Это предотвращает мигание языка при входе
          const storedLang = localStorage.getItem("i18nextLng") || sessionStorage.getItem("i18nextLng");
          if (!storedLang) {
            const browserLang = navigator.language || "en-GB";
            if (i18n.language !== browserLang) {
              await i18n.changeLanguage(browserLang);
            }
          }
        }
      }
    };

    initializeLanguage();
  }, [userId, i18n]);

  const clearLangFromUrl = useCallback(() => {
    if (searchParams.has("lang")) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("lang");
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [searchParams, location.pathname, navigate]);

  const changeUserLanguage = useCallback(
    async (language: string) => {
      const currentUserId = getUserId();
      try {
        if (currentUserId) {
          await changeUserLanguageOnServer(language);
          cachedUserLanguage = language;
          setUserLanguage(currentUserId, language);
          await i18n.changeLanguage(language);
        } else {
          setTempLanguage(language);
          await i18n.changeLanguage(language);
        }

        clearLangFromUrl();
      } catch (error) {
        console.error("Failed to change user language:", error);
        if (currentUserId) {
          cachedUserLanguage = language;
          setUserLanguage(currentUserId, language);
          await i18n.changeLanguage(language);
        } else {
          setTempLanguage(language);
          await i18n.changeLanguage(language);
        }

        clearLangFromUrl();
      }
    },
    [i18n, clearLangFromUrl]
  );

  return {
    changeUserLanguage,
    currentLanguage: i18n.language,
  };
};
